/**
 * ProgramFilter plugin - multi-select filter for grant programs.
 *
 * URL param: programId (singular, multiple values via getAll)
 * API param: programIds (plural)
 * SavedView field: programIds
 *
 * @see docs/specifications/2026-03-10-plugin-filter-architecture.md (Section 4)
 */

import React, { useMemo } from "react";
import { FilterPlugin, FilterComponentProps } from "../types";
import { SavedViewFilters } from "../../../../../services/grantsApi";
import MultiSelectDropdown from "../../../../common/MultiSelectDropdown";
import { ProgramCount } from "../../../../../types/grants";

/**
 * Program filter React component.
 * Renders a multi-select dropdown for program filtering.
 */
const ProgramFilterComponent = React.memo(function ProgramFilterComponent(
  props: FilterComponentProps<string[]>
) {
  const { value, onChange, facets, isLoading } = props;

  const options = useMemo(
    () =>
      ((facets?.programCounts as ProgramCount[]) ?? []).map((program) => ({
        value: program.id,
        label: program.name,
        count: program.count,
      })),
    [facets?.programCounts]
  );

  if (isLoading) {
    return React.createElement("div", {
      className: "w-64 h-10 bg-neutral-200 rounded-md animate-pulse",
    });
  }

  return React.createElement(
    "div",
    { className: "w-64 relative" },
    React.createElement(MultiSelectDropdown, {
      options,
      selectedValues: value,
      onChange,
      placeholder: "Wszystkie programy",
      ariaLabel: "Filtruj według programu",
    })
  );
});

export const programFilter: FilterPlugin<string[]> = {
  key: "program",
  label: "Program",
  placement: "main",
  order: 1,
  defaultValue: [],
  Component: ProgramFilterComponent,

  isActive(value: string[]): boolean {
    return value.length > 0;
  },

  toUrlParams(value: string[], params: URLSearchParams): void {
    value.forEach((id) => params.append("programId", id));
  },

  fromUrlParams(params: URLSearchParams): string[] {
    return params.getAll("programId");
  },

  toSavedView(value: string[], filters: Partial<SavedViewFilters>): void {
    filters.programIds = value;
  },

  fromSavedView(filters: SavedViewFilters): string[] {
    return filters.programIds ?? [];
  },

  toApiParams(value: string[], params: URLSearchParams): void {
    value.forEach((id) => params.append("programIds", id));
  },
};
